// admin-client/src/pages/Users.jsx
// =============================================================================
// Unified Users page.
//
//   - Tab 1 "System Users" (default): backend admins/managers/viewers, with
//     full edit modal (email, display name, CathayID, role, active).
//     Sign-in is central (CXFOP SSO): a pre-created row is claimed by JIT
//     linking (CathayID -> email) at the user's first SSO login; the
//     CathayID also cross-links them to the IPT directory.
//
//   - Tab 2 "IPT Users": CathayID directory built from sign-ins + bookings,
//     with a + System User badge on rows that double as backend users.
//
// Role-based visibility:
//   admin   → full controls (create system user, edit anyone, delete, regen…)
//   manager → can add/edit IPT users; cannot manage system users
//   viewer  → read-only across both tabs (own password only — Profile page)
// =============================================================================

import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { fmtDate, fmtDateTime } from '../lib/datetime.js';
import ImportButton from '../components/ImportButton.jsx';

const PAGE_SIZE = 50;

export default function Users() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState('system'); // 'system' | 'ipt' | 'pending'

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-cathay-700">Users</h1>
          <p className="text-sm text-slate-600">
            Backend accounts (System Users) and the auto-built CathayID directory (IPT Users).
            CathayIDs are the link between them — a System User who is also an IPT user is shown with a badge.
          </p>
        </div>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        <TabButton active={tab === 'system'} onClick={() => setTab('system')}>System Users</TabButton>
        <TabButton active={tab === 'ipt'} onClick={() => setTab('ipt')}>IPT Users</TabButton>
        {me?.role === 'admin' && (
          <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>Pending Users</TabButton>
        )}
      </div>

      {tab === 'system' && <SystemUsersTab me={me} />}
      {tab === 'ipt' && <IptUsersTab me={me} />}
      {tab === 'pending' && me?.role === 'admin' && <PendingUsersTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ' +
        (active
          ? 'border-cathay-600 text-cathay-700'
          : 'border-transparent text-slate-500 hover:text-slate-700')
      }
    >
      {children}
    </button>
  );
}

/* ===========================================================================
 * System Users
 * =========================================================================== */

function SystemUsersTab({ me }) {
  const isAdmin = me?.role === 'admin';
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newForm, setNewForm] = useState({
    email: '', display_name: '', role: 'viewer', cathay_id: '',
  });

  const refresh = async () => {
    try {
      const r = await api.users.list();
      setUsers(r.users);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { refresh(); }, []);

  const [info, setInfo] = useState(null);
  const createUser = async (e) => {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    try {
      const r = await api.users.create({ ...newForm });
      if (r.result === 'pending_review') {
        // Contract: nothing was provisioned; a super-admin must rule first.
        setInfo(r.message || 'Sent for central review — re-add this user after it is ruled.');
      } else {
        if (r.invitation === 'sent') setInfo('User added — a setup invitation email was sent.');
        else if (r.invitation === 'failed') setErr('User added, but the invitation email failed — use the central console to resend.');
        setNewForm({ email: '', display_name: '', role: 'viewer', cathay_id: '' });
        setShowNew(false);
      }
      await refresh();
    } catch (e) {
      setErr(e.message);
    }
  };

  const removeUser = async (id) => {
    if (!confirm('Delete this user permanently?')) return;
    try { await api.users.delete(id); await refresh(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-600">
          Backend admins, managers and viewers. Sign-in happens via CXFOP SSO — pre-create a user
          here with a role, and their central account links to it at first login.
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <a
              href={api.exports.systemUsersCsvUrl()}
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded text-sm"
            >
              Export CSV
            </a>
            <ImportButton onImport={(f) => api.imports.systemUsers(f)} onAfter={refresh}>
              Import CSV
            </ImportButton>
            <button
              onClick={() => setShowNew((v) => !v)}
              className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-2 rounded text-sm"
            >
              {showNew ? 'Cancel' : '+ Add user'}
            </button>
          </div>
        )}
      </div>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
      {info && <div className="bg-cyan-50 text-cyan-800 px-3 py-2 rounded text-sm">{info}</div>}

      {showNew && isAdmin && (
        <form onSubmit={createUser} className="bg-white rounded shadow-sm border p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 text-sm">
          <input
            placeholder="Email"
            required
            type="email"
            value={newForm.email}
            onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
            className="border border-slate-300 rounded px-2 py-1.5"
          />
          <input
            placeholder="Display name (optional)"
            value={newForm.display_name}
            onChange={(e) => setNewForm({ ...newForm, display_name: e.target.value })}
            className="border border-slate-300 rounded px-2 py-1.5"
          />
          <input
            placeholder="CathayID"
            required
            value={newForm.cathay_id}
            onChange={(e) => setNewForm({ ...newForm, cathay_id: e.target.value.toUpperCase() })}
            className="border border-slate-300 rounded px-2 py-1.5 font-mono uppercase"
          />
          <select
            value={newForm.role}
            onChange={(e) => setNewForm({ ...newForm, role: e.target.value })}
            className="border border-slate-300 rounded px-2 py-1.5"
          >
            <option value="viewer">Viewer</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <button className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-2 rounded">
            Create (links at first SSO login)
          </button>
        </form>
      )}

      <div className="bg-white rounded shadow-sm border overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr className="text-left">
              <th className="px-3 py-2">Name / email</th>
              <th className="px-3 py-2">CathayID</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">SSO</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="px-3 py-2">
                  <div className="font-medium">{u.display_name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.cathay_id ? (
                    <span className="font-mono">{u.cathay_id}</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                  {u.has_ipt_user_link && (
                    <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-medium align-middle">
                      IPT
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <RoleBadge role={u.role} />
                  {u.superadmin && (
                    <span
                      title="cxfop-superadmin group member — always effective admin"
                      className="ml-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 align-middle"
                    >
                      superadmin
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={
                    'text-xs ' +
                    (u.is_active ? 'text-slate-700' : 'text-slate-400')
                  }>
                    {u.is_active ? 'active' : 'disabled'}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {u.sso_linked
                    ? <span className="text-green-700" title="Linked to a CXFOP account">linked</span>
                    : <span className="text-slate-500" title="Links at their first SSO login">not yet</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {u.last_login_at ? fmtDateTime(u.last_login_at) : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex flex-wrap gap-1 justify-end text-xs">
                    {isAdmin && (
                      <button onClick={() => setEditing(u)} className="text-cathay-700 hover:underline">
                        Edit
                      </button>
                    )}
                    {isAdmin && u.id !== me?.id && (
                      <button onClick={() => removeUser(u.id)} className="text-red-600 hover:underline">
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">No users yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditSystemUserModal
          user={editing}
          isSelf={editing.id === me?.id}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refresh(); }}
        />
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const colours = {
    admin: 'bg-rose-50 text-rose-700 border-rose-200',
    manager: 'bg-amber-50 text-amber-700 border-amber-200',
    viewer: 'bg-slate-50 text-slate-700 border-slate-200',
    none: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  return (
    <span className={
      'inline-block text-xs px-2 py-0.5 rounded border ' +
      (colours[role] || 'bg-slate-50 text-slate-700 border-slate-200')
    }>
      {role}
    </span>
  );
}

function EditSystemUserModal({ user, isSelf, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: user.email || '',
    display_name: user.display_name || '',
    cathay_id: user.cathay_id || '',
    role: user.role || 'viewer',
    is_active: !!user.is_active,
  });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const patch = {};
      if (form.email !== (user.email || '')) patch.email = form.email.trim();
      if (form.display_name !== (user.display_name || '')) patch.display_name = form.display_name.trim();
      if ((form.cathay_id || '') !== (user.cathay_id || ''))
        patch.cathay_id = form.cathay_id.trim() || null;
      if (!isSelf && form.role !== user.role) patch.role = form.role;
      if (!isSelf && form.is_active !== !!user.is_active) patch.is_active = form.is_active;
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      await api.users.patch(user.id, patch);
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4">
        <header className="flex items-center justify-between">
          <h3 className="font-semibold text-cathay-700">Edit system user</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl">×</button>
        </header>

        {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

        <div className="space-y-3 text-sm">
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-slate-300 rounded px-2 py-1.5"
            />
          </Field>
          <Field label="Display name">
            <input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="w-full border border-slate-300 rounded px-2 py-1.5"
            />
          </Field>
          <Field label="CathayID (JIT-links this row to their CXFOP account at first SSO login; links them to the IPT directory)">
            <input
              value={form.cathay_id}
              onChange={(e) => setForm({ ...form, cathay_id: e.target.value.toUpperCase() })}
              placeholder="e.g. ABCDE"
              className="w-full border border-slate-300 rounded px-2 py-1.5 font-mono uppercase"
            />
          </Field>
          <Field label="Role">
            <select
              value={form.role}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full border border-slate-300 rounded px-2 py-1.5 disabled:bg-slate-100"
            >
              <option value="none">No access — account exists, waiting for a role</option>
              <option value="viewer">Viewer — read-only across all pages</option>
              <option value="manager">Manager — can edit IPT users, sessions, import bookings</option>
              <option value="admin">Admin — full privileges</option>
            </select>
            {isSelf && (
              <p className="text-[11px] text-slate-500 mt-1">You can't change your own role.</p>
            )}
          </Field>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              disabled={isSelf}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span>Active</span>
          </label>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            disabled={busy}
            onClick={save}
            className="bg-cathay-500 hover:bg-cathay-600 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50"
          >
            Save
          </button>
          <button onClick={onClose} className="text-sm px-3 py-1.5 border border-slate-300 rounded">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-600 block mb-1">{label}</span>
      {children}
    </label>
  );
}

/* ===========================================================================
 * Pending Users — the central CXFOP directory minus existing system users.
 * Promote = entitle to IPT + create the local row (app-provisioning contract).
 * =========================================================================== */

function PendingUsersTab() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState('');
  const [roleBySub, setRoleBySub] = useState({});
  const [busySub, setBusySub] = useState(null);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const refresh = async () => {
    setErr(null);
    try {
      const r = await api.users.pending();
      setUsers(r.users);
    } catch (e) {
      setErr(e.message);
      setUsers([]);
    }
  };
  useEffect(() => { refresh(); }, []);

  const promote = async (u) => {
    const role = roleBySub[u.sub] || 'viewer';
    if (!confirm(`Give ${u.cathay_id || u.name || 'this user'} access to IPT as ${role}?`)) return;
    setBusySub(u.sub); setErr(null); setInfo(null);
    try {
      const r = await api.users.promote({ sub: u.sub, role });
      setInfo(
        `${u.cathay_id} promoted to ${role}.` +
        (r.invitation === 'sent' ? ' A setup invitation email was sent.' :
         r.invitation === 'failed' ? ' (Invitation email failed — resend centrally.)' : '')
      );
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusySub(null);
    }
  };

  const needle = q.trim().toLowerCase();
  const shown = (users || []).filter((u) =>
    !needle ||
    (u.cathay_id || '').toLowerCase().includes(needle) ||
    (u.name || '').toLowerCase().includes(needle) ||
    (u.emails || []).some((e) => e.toLowerCase().includes(needle))
  );

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-600">
        Everyone on the CXFOP identity platform who is not yet an IPT system user. Promoting
        grants IPT access centrally and creates the system user with the chosen role —
        credentials always stay with CXFOP.
      </div>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
      {info && <div className="bg-cyan-50 text-cyan-800 px-3 py-2 rounded text-sm">{info}</div>}

      <div className="flex items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search CathayID, name, or email…"
          className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm"
        />
        <button onClick={refresh} className="text-sm border border-slate-300 rounded px-2 py-1.5 hover:bg-slate-50">
          Refresh
        </button>
      </div>

      <div className="bg-white rounded shadow-sm border overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr className="text-left">
              <th className="px-3 py-2">CathayID</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Account</th>
              <th className="px-3 py-2 text-right">Promote</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {shown.map((u) => (
              <tr key={u.sub}>
                <td className="px-3 py-2 font-mono">{u.cathay_id || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2">{u.name || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-xs">{(u.emails && u.emails[0]) || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={
                    u.state === 'active' ? 'text-green-700' :
                    u.state === 'passwordless' ? 'text-amber-700' : 'text-slate-400'
                  }>
                    {u.state}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-2">
                    <select
                      value={roleBySub[u.sub] || 'viewer'}
                      onChange={(e) => setRoleBySub((m) => ({ ...m, [u.sub]: e.target.value }))}
                      className="border border-slate-300 rounded px-1.5 py-1 text-xs"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      disabled={busySub === u.sub || u.state === 'disabled'}
                      onClick={() => promote(u)}
                      className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white px-2 py-1 rounded text-xs"
                    >
                      {busySub === u.sub ? 'Promoting…' : 'Promote'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users !== null && shown.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  {needle ? `No matches for "${q}"` : 'Nobody pending — every directory user is already an IPT system user.'}
                </td>
              </tr>
            )}
            {users === null && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Loading directory…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===========================================================================
 * IPT Users (CathayID directory)
 * =========================================================================== */

function IptUsersTab({ me }) {
  const canEdit = me?.role === 'admin' || me?.role === 'manager';
  const isAdmin = me?.role === 'admin';
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  // sub-keyed directory match per unlinked CathayID (admin only)
  const [suggestions, setSuggestions] = useState({});
  const [linkBusy, setLinkBusy] = useState(null);

  const refresh = async (newOffset = offset) => {
    try {
      const r = await api.iptUsers.list({ q, limit: PAGE_SIZE, offset: newOffset });
      setUsers(r.users);
      setTotal(r.total);
      setOffset(newOffset);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { refresh(0); }, [q]); // eslint-disable-line

  useEffect(() => {
    if (!isAdmin) return;
    api.iptUsers
      .linkSuggestions()
      .then((r) => setSuggestions(
        Object.fromEntries(r.suggestions.map((s) => [s.cathay_id.toUpperCase(), s]))
      ))
      .catch(() => {}); // directory offline/unconfigured — linking simply not offered
  }, [isAdmin]); // eslint-disable-line

  const linkUser = async (cathayId) => {
    const s = suggestions[cathayId.toUpperCase()];
    if (!s) return;
    if (!confirm(
      `Link ${cathayId} to the CXFOP identity "${s.name || s.sub}"?\n\n` +
      'The central account becomes authoritative for identity; local history stays untouched.'
    )) return;
    setLinkBusy(cathayId); setErr(null);
    try {
      await api.iptUsers.link(cathayId, s.sub);
      setSuggestions((m) => { const n = { ...m }; delete n[cathayId.toUpperCase()]; return n; });
      await refresh(offset);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLinkBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-slate-600">
          CathayID directory built automatically from iPad sign-ins and booking uploads.
          Each CathayID appears once — re-imports don't duplicate.
        </div>
        <div className="flex gap-2">
          <a
            href={api.exports.iptUsersCsvUrl()}
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 rounded text-sm"
          >
            Export CSV
          </a>
          {canEdit && (
            <ImportButton onImport={(f) => api.imports.iptUsers(f)} onAfter={() => refresh(0)}>
              Import CSV
            </ImportButton>
          )}
          {canEdit && (
            <button
              onClick={() => setShowNew((v) => !v)}
              className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-2 rounded text-sm"
            >
              {showNew ? 'Cancel' : '+ Add IPT user'}
            </button>
          )}
        </div>
      </div>

      {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {showNew && canEdit && (
        <AddIptUserForm
          onCreated={async () => { setShowNew(false); await refresh(0); }}
          onCancel={() => setShowNew(false)}
        />
      )}

      <div className="flex items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search CathayID or name…"
          className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm"
        />
        <div className="text-xs text-slate-500 whitespace-nowrap">
          {total} total · showing {users.length}
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs">
            <tr className="text-left">
              <th className="px-3 py-2">CathayID</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Link</th>
              <th className="px-3 py-2 text-right">Sessions</th>
              <th className="px-3 py-2 text-right">Bookings</th>
              <th className="px-3 py-2">First seen</th>
              <th className="px-3 py-2">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {users.map((u) => (
              <tr
                key={u.cathay_id}
                onClick={() => setSelected(u.cathay_id)}
                className="cursor-pointer hover:bg-slate-50"
              >
                <td className="px-3 py-2 font-mono">{u.cathay_id}</td>
                <td className="px-3 py-2">
                  {u.oidc_sub
                    ? (u.authoritative_name || u.display_name || <span className="text-slate-400">—</span>)
                    : (u.display_name || <span className="text-slate-400">—</span>)}
                  {u.oidc_sub && (
                    <span
                      title="Linked to a CXFOP identity — name shown is the authoritative directory name"
                      className="ml-1 inline-block px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 text-[10px] border border-cyan-200 align-middle"
                    >
                      CXFOP
                    </span>
                  )}
                  {!u.oidc_sub && suggestions[u.cathay_id?.toUpperCase()] && (
                    <button
                      disabled={linkBusy === u.cathay_id}
                      onClick={(e) => { e.stopPropagation(); linkUser(u.cathay_id); }}
                      title={`Matches CXFOP identity "${suggestions[u.cathay_id.toUpperCase()].name || ''}" (${suggestions[u.cathay_id.toUpperCase()].matched_by})`}
                      className="ml-1 text-[10px] px-1.5 py-0.5 rounded border border-cathay-500 text-cathay-700 hover:bg-cathay-500 hover:text-white align-middle"
                    >
                      {linkBusy === u.cathay_id ? 'Linking…' : 'Link to CXFOP'}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2">
                  {u.is_system_user ? (
                    <span
                      title={`System user: ${u.system_user_display_name || ''} (${u.system_user_role || ''})`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] border border-emerald-200"
                    >
                      + System User
                      {u.system_user_role && (
                        <span className="text-emerald-600">· {u.system_user_role}</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{u.session_count}</td>
                <td className="px-3 py-2 text-right">{u.booking_count}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(u.first_seen_at)}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{fmtDateTime(u.last_seen_at)}</td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  {q ? `No matches for "${q}"` : 'No IPT users yet — they appear once they sign in or are imported.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <button
            disabled={offset === 0}
            onClick={() => refresh(Math.max(0, offset - PAGE_SIZE))}
            className="px-3 py-1.5 border border-slate-300 rounded disabled:opacity-40"
          >
            ← Previous
          </button>
          <div className="text-slate-500">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </div>
          <button
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => refresh(offset + PAGE_SIZE)}
            className="px-3 py-1.5 border border-slate-300 rounded disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}

      {selected && (
        <IptDetailDrawer
          cathayId={selected}
          canEdit={canEdit}
          canDelete={isAdmin}
          onClose={() => setSelected(null)}
          onChanged={() => refresh(offset)}
        />
      )}
    </div>
  );
}

function AddIptUserForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ cathay_id: '', display_name: '', notes: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.iptUsers.create({
        cathay_id: form.cathay_id.trim().toUpperCase(),
        display_name: form.display_name.trim() || null,
        notes: form.notes.trim() || null,
      });
      onCreated();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded shadow-sm border p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm">
      <input
        required
        placeholder="CathayID (2-12 letters)"
        value={form.cathay_id}
        onChange={(e) => setForm({ ...form, cathay_id: e.target.value.toUpperCase() })}
        className="border border-slate-300 rounded px-2 py-1.5 font-mono uppercase"
      />
      <input
        placeholder="Display name (optional)"
        value={form.display_name}
        onChange={(e) => setForm({ ...form, display_name: e.target.value })}
        className="border border-slate-300 rounded px-2 py-1.5"
      />
      <input
        placeholder="Notes (optional)"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        className="border border-slate-300 rounded px-2 py-1.5"
      />
      <div className="flex gap-2">
        <button disabled={busy} className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-2 rounded disabled:opacity-50">
          Create
        </button>
        <button type="button" onClick={onCancel} className="border border-slate-300 px-3 py-2 rounded">
          Cancel
        </button>
      </div>
      {err && <div className="sm:col-span-4 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
    </form>
  );
}

function IptDetailDrawer({ cathayId, canEdit, canDelete, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ display_name: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.iptUsers.get(cathayId);
      setData(r);
      setForm({ display_name: r.user.display_name || '', notes: r.user.notes || '' });
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => { load(); }, [cathayId]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      await api.iptUsers.patch(cathayId, form);
      setEditing(false);
      await load();
      onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!confirm(`Remove ${cathayId} from the directory? Their sessions and bookings stay intact.`)) return;
    setBusy(true);
    try {
      await api.iptUsers.delete(cathayId);
      onChanged();
      onClose();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/30 flex justify-end" onClick={onClose}>
      <aside
        className="w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-cathay-700 font-mono">{cathayId}</h2>
            {data?.user.display_name && (
              <div className="text-sm text-slate-600">{data.user.display_name}</div>
            )}
            {data?.user.is_system_user && (
              <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] border border-emerald-200">
                + System User: {data.user.system_user_display_name} ({data.user.system_user_role})
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-xl">×</button>
        </header>

        <div className="p-4 space-y-6">
          {err && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

          {data ? (
            <>
              <section className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                <Stat label="Sessions" value={data.stats.total_sessions} />
                <Stat label="Hours used" value={Math.round((data.stats.total_minutes_used / 60) * 10) / 10} />
                <Stat label="Bookings" value={data.stats.total_bookings} />
                <Stat label="Hours booked" value={data.stats.total_booked_hours} />
              </section>

              <section className="bg-slate-50 rounded p-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-sm">Profile</h3>
                  {canEdit && !editing && (
                    <button onClick={() => setEditing(true)} className="text-xs text-cathay-700 hover:underline">
                      Edit
                    </button>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2 mt-2 text-sm">
                    <label className="block">
                      <span className="text-xs text-slate-600">Display name</span>
                      <input
                        value={form.display_name}
                        onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-600">Notes</span>
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3}
                        className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5"
                      />
                    </label>
                    <div className="flex gap-2 pt-1">
                      <button
                        disabled={busy}
                        onClick={save}
                        className="bg-cathay-500 hover:bg-cathay-600 text-white text-sm px-3 py-1.5 rounded"
                      >
                        Save
                      </button>
                      <button onClick={() => setEditing(false)} className="text-sm px-3 py-1.5 border rounded">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-sm space-y-1">
                    <div><span className="text-slate-500">Name:</span> {data.user.display_name || '—'}</div>
                    <div><span className="text-slate-500">Notes:</span> {data.user.notes || '—'}</div>
                    <div className="text-xs text-slate-500">
                      First seen {fmtDateTime(data.user.first_seen_at)} · Last seen {fmtDateTime(data.user.last_seen_at)}
                    </div>
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-sm font-medium text-slate-700 mb-2">
                  Recent sessions ({data.sessions.length})
                </h3>
                <table className="min-w-full text-xs">
                  <tbody>
                    {data.sessions.map((s) => (
                      <tr key={s.id} className="border-t border-slate-100">
                        <td className="py-1 w-32">{fmtDateTime(s.signin_at_utc)}</td>
                        <td className="py-1 w-24">
                          {s.signout_at_utc ? fmtDateTime(s.signout_at_utc) : <em className="text-amber-600">open</em>}
                        </td>
                        <td className="py-1">{s.device_name}</td>
                        <td className="py-1 text-slate-500">{s.auto_closed ? 'auto-closed' : ''}</td>
                      </tr>
                    ))}
                    {data.sessions.length === 0 && (
                      <tr><td className="py-1 text-slate-500">No sessions yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </section>

              <section>
                <h3 className="text-sm font-medium text-slate-700 mb-2">
                  Recent bookings ({data.bookings.length})
                </h3>
                <table className="min-w-full text-xs">
                  <tbody>
                    {data.bookings.map((b) => (
                      <tr key={b.id} className="border-t border-slate-100">
                        <td className="py-1 w-24">{fmtDate(b.booking_date)}</td>
                        <td className="py-1 w-32">
                          {String(b.start_hour).padStart(2, '0')}:00 – {String(b.end_hour % 24).padStart(2, '0')}:00
                          {b.end_hour > 24 ? ' (+1d)' : ''}
                        </td>
                        <td className="py-1">{b.device_name || <span className="text-slate-400">any IPT</span>}</td>
                      </tr>
                    ))}
                    {data.bookings.length === 0 && (
                      <tr><td className="py-1 text-slate-500">No bookings yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </section>

              {canDelete && (
                <section className="pt-4 border-t border-slate-100">
                  <button onClick={remove} disabled={busy} className="text-sm text-red-600 hover:underline">
                    Remove from directory
                  </button>
                  <p className="text-xs text-slate-500 mt-1">
                    Historic sessions and bookings stay. The directory entry will reappear if this CathayID is seen again.
                  </p>
                </section>
              )}
            </>
          ) : (
            <div className="text-sm text-slate-500">Loading…</div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded p-2 border border-slate-200">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}
